// src/pages/sqf/SqfChecklistListPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import sqfService from '../../services/sqfService';
import { useLanguage, t } from '../../context/LanguageContext';
import SqfNav from '../../components/SqfNav';
import StartVisualInspectionModal from '../../components/StartVisualInspectionModal';

const displayName = (u) => (u?.firstName || u?.lastName) ? `${u.firstName || ''} ${u.lastName || ''}`.trim() : (u?.username || '—');

const STATUS_BADGE = {
  pending:     { label: 'Pending',     color: 'secondary' },
  in_progress: { label: 'In Progress', color: 'warning'   },
  submitted:   { label: 'Submitted',   color: 'info'      },
  qa_approved: { label: 'QA Approved', color: 'success'   },
  qa_rejected: { label: 'QA Rejected', color: 'danger'    },
  overdue:     { label: 'Overdue',     color: 'danger'    },
};

export default function SqfChecklistListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'all';

  const { lang } = useLanguage();
  const [instances, setInstances] = useState([]);
  const [pendingQa, setPendingQa] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filter state
  const [filterDate, setFilterDate] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterTemplate, setFilterTemplate] = useState('');

  // New instance modal
  const [showNewModal, setShowNewModal] = useState(false);
  const [newTemplateId, setNewTemplateId] = useState('');
  const [newDate, setNewDate] = useState(new Date().toISOString().split('T')[0]);
  const [newShift, setNewShift] = useState('na');
  const [creating, setCreating] = useState(false);
  const [showVisualInspectionModal, setShowVisualInspectionModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterDate) params.date = filterDate;
      if (filterStatus) params.status = filterStatus;
      if (filterTemplate) params.templateId = filterTemplate;
      const [inst, qa, tmpl] = await Promise.all([
        sqfService.getInstances(params),
        sqfService.getPendingQaLogs(),
        sqfService.getTemplates(),
      ]);
      // Deduplicate by id — guards against race-condition duplicates from the gate endpoint
      const seen = new Set();
      setInstances(inst.filter(i => { if (seen.has(i.id)) return false; seen.add(i.id); return true; }));
      const seenQa = new Set();
      setPendingQa(qa.filter(l => { if (seenQa.has(l.id)) return false; seenQa.add(l.id); return true; }));
      setTemplates(tmpl);
    } catch {
      setError(t('Failed to load checklists.', 'Error al cargar las listas de verificación.', lang));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDate, filterStatus, filterTemplate]);

  useEffect(() => { load(); }, [load]);

  // A Visual Inspection template needs a real batch/product to check against — creating a bare
  // instance here (date/shift only, no batch/product) used to leave the Fill page in a permanent
  // "no linked product/batch" error with no way to attach one after the fact. Defer to the shared
  // launcher instead, which collects a real recent lot/product before creating the instance.
  const selectedNewTemplate = templates.find(tmpl => String(tmpl.id) === String(newTemplateId));

  const handleCreateInstance = async () => {
    if (!newTemplateId) return;
    if (selectedNewTemplate?.requiresBatchProduct) {
      setShowNewModal(false);
      setShowVisualInspectionModal(true);
      return;
    }
    setCreating(true);
    try {
      const inst = await sqfService.createInstance({ templateId: newTemplateId, dueDate: newDate, shift: newShift });
      setShowNewModal(false);
      navigate(`/sqf/fill/${inst.id}`);
    } catch {
      setError(t('Failed to create checklist.', 'Error al crear la lista de verificación.', lang));
    } finally {
      setCreating(false);
    }
  };

  const statusBadgeLabel = (status, lang) => {
    const labelMap = {
      'Pending':     t('Pending',     'Pendiente',   lang),
      'In Progress': t('In Progress', 'En progreso', lang),
      'Submitted':   t('Submitted',   'Enviado',     lang),
      'QA Approved': t('QA Approved', 'Aprobado QA', lang),
      'QA Rejected': t('QA Rejected', 'Rechazado QA', lang),
      'Overdue':     t('Overdue',     'Vencido',     lang),
    };
    return labelMap[status] || status;
  };

  const instanceAction = (inst) => {
    if (['pending', 'in_progress'].includes(inst.status)) {
      return <button className="btn btn-primary btn-sm" onClick={() => navigate(`/sqf/fill/${inst.id}`)}>{t('Fill', 'Completar', lang)}</button>;
    }
    if (inst.Log) {
      return <Link to={`/sqf/logs/${inst.Log.id}`} className="btn btn-outline-secondary btn-sm">{t('View', 'Ver', lang)}</Link>;
    }
    return null;
  };

  return (
    <>
      <SqfNav />
      <div className="container mt-4 pb-5">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div>
          <Link to="/sqf" className="text-muted" style={{ fontSize: 12 }}>← {t('Dashboard', 'Panel', lang)}</Link>
          <h4 className="mb-0 mt-1">{t('Checklists', 'Listas de verificación', lang)}</h4>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowNewModal(true)}>+ {t('On-Demand', 'Bajo demanda', lang)}</button>
      </div>

      {error && <div className="alert alert-danger py-2">{error}<button className="btn-close float-end btn-sm" onClick={() => setError('')} /></div>}

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button className={`nav-link ${tab === 'all' ? 'active' : ''}`} onClick={() => setSearchParams({ tab: 'all' })}>
            {t('All Instances', 'Todas las instancias', lang)}
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${tab === 'pending-qa' ? 'active' : ''}`} onClick={() => setSearchParams({ tab: 'pending-qa' })}>
            {t('Pending QA', 'QA pendiente', lang)} {pendingQa.length > 0 && <span className="badge bg-info ms-1">{pendingQa.length}</span>}
          </button>
        </li>
      </ul>

      {tab === 'all' && (
        <>
          {/* Filters */}
          <div className="row g-2 mb-3">
            <div className="col-auto">
              <input type="date" className="form-control form-control-sm" value={filterDate}
                onChange={e => setFilterDate(e.target.value)} />
            </div>
            <div className="col-auto">
              <select className="form-select form-select-sm" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                <option value="">{t('All statuses', 'Todos los estados', lang)}</option>
                {Object.entries(STATUS_BADGE).map(([k, v]) => <option key={k} value={k}>{statusBadgeLabel(v.label, lang)}</option>)}
              </select>
            </div>
            <div className="col-auto">
              <select className="form-select form-select-sm" value={filterTemplate} onChange={e => setFilterTemplate(e.target.value)}>
                <option value="">{t('All templates', 'Todas las plantillas', lang)}</option>
                {templates.map(tmpl => <option key={tmpl.id} value={tmpl.id}>{tmpl.name}</option>)}
              </select>
            </div>
            {(filterDate || filterStatus || filterTemplate) && (
              <div className="col-auto">
                <button className="btn btn-outline-secondary btn-sm" onClick={() => { setFilterDate(''); setFilterStatus(''); setFilterTemplate(''); }}>
                  {t('Clear', 'Limpiar', lang)}
                </button>
              </div>
            )}
          </div>

          {loading ? (
            <div className="text-center text-muted py-4"><div className="spinner-border spinner-border-sm me-2" />{t('Loading…', 'Cargando…', lang)}</div>
          ) : instances.length === 0 ? (
            <div className="text-center text-muted py-5">{t('No checklists found.', 'No se encontraron listas de verificación.', lang)}</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-hover table-bordered">
                <thead className="table-light">
                  <tr>
                    <th>{t('Template', 'Plantilla', lang)}</th>
                    <th>{t('Due Date', 'Fecha de vencimiento', lang)}</th>
                    <th>{t('Shift', 'Turno', lang)}</th>
                    <th>{t('Status', 'Estado', lang)}</th>
                    <th>{t('Assigned To', 'Asignado a', lang)}</th>
                    <th style={{ width: 90 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {instances.map(inst => (
                    <tr key={inst.id}>
                      <td className="fw-semibold">{t(inst.Template?.name, inst.Template?.nameEs, lang)}</td>
                      <td>{inst.dueDate}</td>
                      <td className="text-capitalize">{inst.shift !== 'na' ? inst.shift : '—'}</td>
                      <td>
                        {(() => { const cfg = STATUS_BADGE[inst.status] || { label: inst.status, color: 'secondary' }; return <span className={`badge bg-${cfg.color}`}>{statusBadgeLabel(cfg.label, lang)}</span>; })()}
                        {inst.Log?.hasDeviation && <span className="badge bg-warning text-dark ms-1" style={{ fontSize: 10 }}>⚠ {t('Dev.', 'Desv.', lang)}</span>}
                      </td>
                      <td style={{ fontSize: 12 }}>
                        {inst.AssignedUser ? displayName(inst.AssignedUser) : '—'}
                      </td>
                      <td className="text-end">{instanceAction(inst)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === 'pending-qa' && (
        loading ? (
          <div className="text-center text-muted py-4"><div className="spinner-border spinner-border-sm me-2" />{t('Loading…', 'Cargando…', lang)}</div>
        ) : pendingQa.length === 0 ? (
          <div className="text-center text-muted py-5">{t('No logs pending QA review.', 'No hay registros pendientes de revisión QA.', lang)}</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm table-hover table-bordered">
              <thead className="table-light">
                <tr>
                  <th>{t('Template', 'Plantilla', lang)}</th>
                  <th>{t('Submitted', 'Enviado', lang)}</th>
                  <th>{t('Filled By', 'Completado por', lang)}</th>
                  <th>{t('Deviation', 'Desviación', lang)}</th>
                  <th style={{ width: 90 }}></th>
                </tr>
              </thead>
              <tbody>
                {pendingQa.map(log => (
                  <tr key={log.id}>
                    <td className="fw-semibold">{log.Instance?.Template?.name}</td>
                    <td style={{ fontSize: 12 }}>{log.submittedAt ? new Date(log.submittedAt).toLocaleString() : '—'}</td>
                    <td style={{ fontSize: 12 }}>{log.FilledBy ? displayName(log.FilledBy) : '—'}</td>
                    <td>{log.hasDeviation ? <span className="badge bg-warning text-dark">⚠ {t('Yes', 'Sí', lang)}</span> : <span className="badge bg-success">{t('None', 'Ninguna', lang)}</span>}</td>
                    <td className="text-end"><Link to={`/sqf/logs/${log.id}`} className="btn btn-outline-primary btn-sm">{t('Review', 'Revisar', lang)}</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* On-demand modal */}
      {showNewModal && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('New On-Demand Checklist', 'Nueva lista de verificación bajo demanda', lang)}</h5>
                <button className="btn-close" onClick={() => setShowNewModal(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label fw-semibold">{t('Template', 'Plantilla', lang)}</label>
                  <select className="form-select" value={newTemplateId} onChange={e => setNewTemplateId(e.target.value)}>
                    <option value="">{t('Select template…', 'Seleccionar plantilla…', lang)}</option>
                    {templates.filter(tmpl => !tmpl.archivedAt && tmpl.activeVersionId).map(tmpl => (
                      <option key={tmpl.id} value={tmpl.id}>{tmpl.name}</option>
                    ))}
                  </select>
                </div>
                {selectedNewTemplate?.requiresBatchProduct ? (
                  <div className="alert alert-info py-2" style={{ fontSize: 13 }}>
                    {t(
                      'This is a Visual Inspection template — it needs to be tied to a real production lot/product. You\'ll pick one on the next step.',
                      'Esta es una plantilla de Inspección Visual — debe vincularse a un lote/producto de producción real. Lo seleccionará en el siguiente paso.',
                      lang
                    )}
                  </div>
                ) : (
                  <>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">{t('Date', 'Fecha', lang)}</label>
                      <input type="date" className="form-control" value={newDate} onChange={e => setNewDate(e.target.value)} />
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">{t('Shift', 'Turno', lang)}</label>
                      <select className="form-select" value={newShift} onChange={e => setNewShift(e.target.value)}>
                        <option value="na">N/A</option>
                        <option value="morning">{t('Morning', 'Mañana', lang)}</option>
                        <option value="afternoon">{t('Afternoon', 'Tarde', lang)}</option>
                        <option value="night">{t('Night', 'Noche', lang)}</option>
                      </select>
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowNewModal(false)}>{t('Cancel', 'Cancelar', lang)}</button>
                <button className="btn btn-primary" onClick={handleCreateInstance} disabled={creating || !newTemplateId}>
                  {creating
                    ? t('Creating…', 'Creando…', lang)
                    : selectedNewTemplate?.requiresBatchProduct
                      ? t('Continue →', 'Continuar →', lang)
                      : t('Start Checklist', 'Iniciar lista de verificación', lang)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showVisualInspectionModal && selectedNewTemplate && (
        <StartVisualInspectionModal
          presetTemplateId={selectedNewTemplate.id}
          presetTemplateName={selectedNewTemplate.name}
          onClose={() => setShowVisualInspectionModal(false)}
        />
      )}
    </div>
    </>
  );
}
